// -----------------------------------------------------------------------------
// UploadDocumentPanel  (Wave C — minimal)
//
// Action panel body for UPLOAD_DOCUMENT.
// Provides a simple file input with drag-drop visual. Uploads to the lead
// media API (POST /practices/{p}/leads/{l}/media/) then calls onComplete.
//
// The full ChecklistItemRow.jsx upload pipeline (camera shortcut, recent media
// attach, split-button UX) is not replicated here — this is a minimal drop
// area. Marked as minimal in the Task #16 report.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState, useRef, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    LinearProgress,
    Stack,
} from '@mui/material';
import { dispatch } from 'src/redux/store';
import { updateError } from 'src/redux/slices/user';
import { callApi } from 'src/utils/apiCall';
import Iconify from 'src/components/Iconify';

const MAX_FILE_SIZE_MB = 25;

export default function UploadDocumentPanel({
    item,
    practiceId,
    leadId,
    onComplete,
    onClose,
}) {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileSelect = (selected) => {
        if (!selected) return;
        if (selected.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            dispatch(updateError({ success: false, message: `File must be under ${MAX_FILE_SIZE_MB}MB` }));
            return;
        }
        setFile(selected);
    };

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragging(false);
        const dropped = e.dataTransfer.files?.[0];
        if (dropped) handleFileSelect(dropped);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = () => setIsDragging(false);

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('lead', leadId);
            formData.append('description', file.name);

            const res = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/media/`,
                'post',
                formData
            );
            if (res.status === 201) {
                dispatch(updateError({ success: true, message: 'Document uploaded' }));
                onComplete?.(item);
            } else {
                const msg = res?.data?.detail || res?.data?.file?.[0] || 'Upload failed';
                dispatch(updateError({ success: false, message: msg }));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Upload failed' }));
        } finally {
            setUploading(false);
        }
    };

    return (
        <Box
            data-testid="upload-document-panel"
            sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.75,
                height: '100%',
                overflowY: 'auto',
            }}
        >
            {/* Icon header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: 'rgba(54, 179, 126, 0.10)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Iconify icon="mdi:upload-outline" width={18} height={18} sx={{ color: '#36B37E' }} />
                </Box>
                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.012em' }}>
                    Upload document
                </Typography>
            </Box>

            {/* Drop zone */}
            <Box
                data-testid="upload-drop-zone"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `2px dashed ${isDragging ? '#36B37E' : 'rgba(0,0,0,0.12)'}`,
                    borderRadius: '12px',
                    bgcolor: isDragging ? 'rgba(54, 179, 126, 0.04)' : '#F5F5F7',
                    py: 2.5,
                    px: 2,
                    cursor: 'pointer',
                    transition: 'border-color 200ms, background-color 200ms',
                    textAlign: 'center',
                    minHeight: 100,
                    '&:hover': { borderColor: '#36B37E', bgcolor: 'rgba(54, 179, 126, 0.04)' },
                }}
            >
                {file ? (
                    <>
                        <Iconify icon="mdi:file-outline" width={28} height={28} sx={{ color: '#36B37E', mb: 0.75 }} />
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1d1d1f' }}>
                            {file.name}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: '#6e6e73', mt: 0.25 }}>
                            {(file.size / 1024).toFixed(1)} KB · Click to change
                        </Typography>
                    </>
                ) : (
                    <>
                        <Iconify icon="mdi:cloud-upload-outline" width={28} height={28} sx={{ color: '#6e6e73', mb: 0.75 }} />
                        <Typography sx={{ fontSize: '13px', color: '#6e6e73' }}>
                            Drag & drop or <Box component="span" sx={{ color: '#36B37E', fontWeight: 600 }}>browse</Box>
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: '#aeaeb2', mt: 0.25 }}>
                            Max {MAX_FILE_SIZE_MB}MB
                        </Typography>
                    </>
                )}
            </Box>

            {uploading && <LinearProgress sx={{ borderRadius: '2px' }} />}

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
                data-testid="upload-file-input"
            />

            {/* Actions */}
            <Stack direction="row" spacing={1}>
                <Button
                    data-testid="upload-submit"
                    variant="contained"
                    disableElevation
                    onClick={handleUpload}
                    disabled={!file || uploading}
                    startIcon={uploading ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Iconify icon="mdi:upload" width={14} height={14} />}
                    sx={{
                        flex: 1,
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 600,
                        bgcolor: '#36B37E',
                        '&:hover': { bgcolor: '#2d9e6e' },
                        boxShadow: 'none',
                    }}
                >
                    {uploading ? 'Uploading…' : 'Upload'}
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={onClose}
                    disabled={uploading}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '13px' }}
                >
                    Cancel
                </Button>
            </Stack>
        </Box>
    );
}

UploadDocumentPanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onComplete: PropTypes.func,
    onClose: PropTypes.func.isRequired,
};

UploadDocumentPanel.defaultProps = {
    item: null,
    practiceId: null,
    leadId: null,
    onComplete: null,
};
